import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArchive, faUndo, faSearch } from '@fortawesome/free-solid-svg-icons';
import type { Conversation } from '../types';

const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';

interface ArchivedModalProps {
  isOpen: boolean;
  onClose: () => void;
  archivedConversations: Conversation[];
  onRestoreConversation: (conversationId: string) => void;
}

export const ArchivedModal: React.FC<ArchivedModalProps> = ({
  isOpen,
  onClose,
  archivedConversations,
  onRestoreConversation,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return archivedConversations;
    }
    const query = searchQuery.toLowerCase();
    return archivedConversations.filter(
      (conv) =>
        conv.title.toLowerCase().includes(query) ||
        (conv.communityLabel && conv.communityLabel.toLowerCase().includes(query))
    );
  }, [archivedConversations, searchQuery]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[500px] mx-4"
        style={{
          backgroundColor: 'white',
          borderRadius: '4px',
          maxHeight: '85vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-4 md:p-6"
          style={{
            borderBottom: '1px solid rgba(229, 231, 235, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              className="w-10 h-10 md:w-12 md:h-12"
              style={{
                borderRadius: '50%',
                backgroundColor: ACCENT_COLOR,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon
                icon={faArchive}
                className="w-5 h-5 md:w-6 md:h-6"
                style={{ color: 'white' }}
              />
            </div>
            <div>
              <h2
                className="text-base md:text-lg"
                style={{
                  fontWeight: 600,
                  color: DARK_COLOR,
                  margin: 0,
                  fontFamily: 'Lato, system-ui, sans-serif',
                }}
              >
                Archived Conversations
              </h2>
              <p
                className="text-xs md:text-sm hidden md:block"
                style={{
                  color: '#666',
                  margin: 0,
                  fontFamily: 'Lato, system-ui, sans-serif',
                }}
              >
                Restore conversations to make them active again
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Close"
          >
            <FontAwesomeIcon
              icon={faTimes}
              style={{ color: DARK_COLOR, width: 16, height: 16 }}
            />
          </button>
        </div>

        {/* Search Bar */}
        {archivedConversations.length > 0 && (
          <div
            className="px-4 md:px-6 py-3"
            style={{
              borderBottom: '1px solid rgba(229, 231, 235, 0.3)',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  color: '#999',
                  width: 14,
                  height: 14,
                }}
              />
              <input
                type="text"
                placeholder="Search archived conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-sm"
                style={{
                  width: '100%',
                  padding: '0.625rem 0.75rem 0.625rem 2.25rem',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.875rem',
                  fontFamily: 'Lato, system-ui, sans-serif',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = ACCENT_COLOR;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className="px-4 md:px-6 py-4"
          style={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          {archivedConversations.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem 1rem',
                color: '#666',
                fontFamily: 'Lato, system-ui, sans-serif',
                fontSize: '0.875rem',
              }}
            >
              No archived conversations yet.
            </div>
          ) : filteredConversations.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem 1rem',
                color: '#666',
                fontFamily: 'Lato, system-ui, sans-serif',
                fontSize: '0.875rem',
              }}
            >
              No conversations match "{searchQuery}"
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className="flex-col md:flex-row md:items-center"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.875rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '4px',
                    fontFamily: 'Lato, system-ui, sans-serif',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      className="text-sm"
                      style={{
                        fontWeight: 500,
                        color: DARK_COLOR,
                        display: 'block',
                        marginBottom: '0.125rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={conversation.title}
                    >
                      {conversation.title}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span
                        className="text-xs"
                        style={{
                          color: '#666',
                        }}
                      >
                        {conversation.communityLabel || 'No Community Selected'}
                      </span>
                      <span
                        className="text-xs"
                        style={{
                          color: '#999',
                        }}
                      >
                        {new Date(conversation.updatedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => onRestoreConversation(conversation.id)}
                    className="w-full md:w-auto"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: DARK_COLOR,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      fontFamily: 'Lato, system-ui, sans-serif',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = ACCENT_COLOR;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = DARK_COLOR;
                    }}
                  >
                    <FontAwesomeIcon icon={faUndo} style={{ width: 12, height: 12 }} />
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-4 md:px-6 py-3 md:py-4"
          style={{
            borderTop: '1px solid rgba(229, 231, 235, 0.5)',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            className="w-full md:w-auto"
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: ACCENT_COLOR,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              fontFamily: 'Lato, system-ui, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f16952';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = ACCENT_COLOR;
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArchivedModal;
